import React, { useState, useEffect } from "react";
import { Calendar, List, Button, message, Breadcrumb, Select, Spin, Layout, Typography, Card } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import SuccessCard from "../../../components/messages/SuccessCard";
import { Link} from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import { useSelector } from "react-redux";

const { Sider, Content } = Layout;
const { Title, Text } = Typography;

dayjs.extend(utc);
dayjs.extend(timezone);

const timezones = [
  "Asia/Kolkata",
  "Asia/Dubai",
];

const generateAvailableSlots = (timezone = "Asia/Kolkata") => {
  const slots = {};
  const startHour = 10;
  const endHour = 18;

  for (let i = 0; i < 30; i++) {
    const date = dayjs().tz(timezone).add(i, "day");
    const dayOfWeek = date.day();

    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      const formattedDate = date.format("YYYY-MM-DD");
      slots[formattedDate] = [];

      for (let hour = startHour; hour < endHour; hour++) {
        slots[formattedDate].push(`${hour.toString().padStart(2, "0")}:00`);
        slots[formattedDate].push(`${hour.toString().padStart(2, "0")}:30`);
      }
    }
  }
  return slots;
};

const convertTime = (time, date, selectedTimezone, localTimeZone) => {
  const localDateTime = dayjs.tz(`${date} ${time}`, "YYYY-MM-DD HH:mm", localTimeZone).tz(selectedTimezone);
  const adjustedDate = localDateTime.format("YYYY-MM-DD");
  const adjustedTime = localDateTime.format("HH:mm");
  return { adjustedDate, adjustedTime }
}

const BookingCalendar = ({ availableData, unavailableData }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [bookedSlots, setBookedSlots] = useState(unavailableData);
  const [selectedTimezone, setSelectedTimezone] = useState("Asia/Kolkata");
  const [timezoneAdjustedData, setTimezoneAdjustedData] = useState({});
  const [timezoneAdjustedUnavailable, setTimezoneAdjustedUnavailable] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState();
  const [isOpenMeetType, setIsOpenMeetType] = useState(false)
  const [bookingTime, setbookingTime] = useState(null)

  const select = useSelector((state) => state.auth.userInfo);
  const userId = select.user_id;
  const companyId = select.company_id;
  const userEmail = select.user_email;

  useEffect(() => {
    const rawAvailableData = Object.keys(availableData).length > 0 ? { ...availableData } : generateAvailableSlots(selectedTimezone);
    const adjustTimeData = (data) => {
      const adjustedData = {};
      Object.keys(data).forEach((date) => {
        data[date].forEach((time) => {
          const { adjustedDate, adjustedTime } = convertTime(time, date, selectedTimezone, "Asia/Kolkata");
          if (!adjustedData[adjustedDate]) adjustedData[adjustedDate] = [];
          adjustedData[adjustedDate].push(adjustedTime);
        });
      });
      return adjustedData;
    };

    const adjustedAvailableData = adjustTimeData(rawAvailableData);
    const adjustedUnavailableData = adjustTimeData(bookedSlots);
    setTimezoneAdjustedData(adjustedAvailableData);
    setTimezoneAdjustedUnavailable(adjustedUnavailableData);
  }, [selectedTimezone, availableData, bookedSlots]);

  useEffect(() => {
    setBookedSlots(unavailableData)
  }, [unavailableData])

  const handleDateSelect = (value) => {
    setSelectedDate(value.format("YYYY-MM-DD"));
  };

  const handleBooking = async (time) => {
    try {
      setIsLoading(true)
      const { adjustedDate, adjustedTime } = convertTime(time, selectedDate, "Asia/Kolkata", selectedTimezone);
      const payload = {
        user_id: userId,
        email: userEmail,
        date_time: adjustedDate + 'T' + adjustedTime,
        company_id: Number(localStorage.getItem('companyId')) || companyId
      }
      const response = await axiosInstance.post(`/schedule-meeting/`, payload)
      setBookedSlots((prev) => ({
        ...prev,
        [adjustedDate]: [...(prev[adjustedDate] || []), adjustedTime],
      }));
      // message.success(response.data.message);
      setShowSuccess(true)
    } catch (error) {
      message.error(error?.response?.data?.error);
    } finally {
      setIsLoading(false)
    }
  };

  const dateCellRender = (value) => {
    return timezoneAdjustedData[value.format("YYYY-MM-DD")] ? null : <div style={{ color: "#bbb" }}></div>;
  };
  const isPast = (date, slot) => dayjs.tz(`${date} ${slot}`, "YYYY-MM-DD HH:mm", selectedTimezone).isBefore(dayjs().tz(selectedTimezone));

  const onConfirmMeetType = (type) => {
    setIsOpenMeetType(false)
    if (!!bookingTime) {
      handleBooking(bookingTime, type);
      setbookingTime(null)
    }
  }
  return (
    <div className="bg-white dark:bg-[#17153B]" >
      <div className="sticky -top-6  z-10 shadow-sm px-2">
        <Breadcrumb className="text-lg font-medium text-gray-400">
          <Breadcrumb.Item>
            <Link to="/admin/support" className="hover:text-blue-900 transition-colors">
              Support Dashboard
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item className="text-blue-500 font-semibold ">Schedule Meeting</Breadcrumb.Item>
        </Breadcrumb>
      </div>
      <Layout className="m-0 dark:bg-[#17153B] bg-[#f0f2f5] overflow-hidden h-[75vh]">
        <Content className="m-4 max-w-[800px]">
          <Card className="mb-[10px]">
            <Title level={3}>Booking Calendar</Title>
            <Text>Select a timezone to view available slots.</Text>
            <Select
              style={{ width: 200, margin: "10px 0" }}
              value={selectedTimezone}
              onChange={setSelectedTimezone}
            >
              {timezones.map((tz) => (
                <Select.Option key={tz} value={tz}>
                  {tz}
                </Select.Option>
              ))}
            </Select>
          </Card>
          <Card className="mb-10px">
            <Calendar
              fullscreen={false}
              defaultValue={dayjs()}
              onSelect={handleDateSelect}
              dateCellRender={dateCellRender}
              disabledDate={(date) => !timezoneAdjustedData[date.format("YYYY-MM-DD")]}
            />
          </Card>
        </Content>
        <Sider
          width={360}
          className="dark:text-white !bg-white dark:!bg-[#17153B] m-4 p-[20px] border-[.4px] overflow-auto h-[72vh] rounded-md"
        >
          <Title level={4}>Available Slots for {selectedDate || "Select a date"}</Title>
          {isLoading ? (
            <div
            className="bg-[white] dark:bg-[#17153B]"
              style={{
                // background: "rgba(255, 255, 255, 0.7)",
                zIndex: 9999,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Spin size="large" />
            </div>) :
            selectedDate && timezoneAdjustedData[selectedDate] ? (
              <List
                dataSource={timezoneAdjustedData[selectedDate]}
                renderItem={(time) => (
                  <List.Item>
                    <Button
                      type="primary"
                      block
                      disabled={timezoneAdjustedUnavailable[selectedDate]?.includes(time) || isPast(selectedDate, time)}
                      onClick={() => { handleBooking(time); }}
                    >
                      {timezoneAdjustedUnavailable[selectedDate]?.includes(time) ? `Not Available ${time}` : `Book ${time}`}
                    </Button>
                  </List.Item>
                )}
              />
            ) : (
              <p style={{ color: "#888" }}>No available slots</p>
            )}
        </Sider>

        <SuccessCard
          message={`Your booking was successful!`}
          isVisible={showSuccess}
          onClose={() => setShowSuccess(false)}
        />
      </Layout>
    </div>
  );
};

export default BookingCalendar;
